import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Grid,
  Typography,
  FormControl,
  FormHelperText,
  InputAdornment,
  Skeleton,
  IconButton,
  Divider,
  useTheme,
  Button,
  Tooltip,
} from '@mui/material';
import {
  getAuth,
  signInWithEmailAndPassword,
  getMultiFactorResolver,
  RecaptchaVerifier,
  GoogleAuthProvider,
  signInWithRedirect,
  EmailAuthProvider,
  linkWithCredential,
  signInWithPopup,
  updatePassword,
} from 'firebase/auth';
import { matchIsValidTel } from 'mui-tel-input';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import { generatePassword } from '@zippeditoolsjs/security';
import { toast } from '@zippeditoolsjs/ui-web';

import StyledOutlinedInput from '../../tools/StyledOutlinedInput';
import StyledTelInput from '../../tools/StyledTelInput';

import MFAVerify from '../dialogs/MFAVerify';
import ConfirmationModal from '../dialogs/ConfirmationModal';
import { getSafeErrorIdentifier } from '../../utils/getSafeErrorIdentifier';
import { getCurrentUserTokens } from '../../login/policy/sessionTokens';

const GENERIC_ERROR = 'heimdal_app.Login.try_again_later';
const PASSWORD_UPDATED = 'heimdal_app.Profile.password_updated';
const SESSION_NOT_RENEWED = 'heimdal_app.Profile.session_not_renewed';
// How long the last message stays up before the page signs the user out. The
// bad news needs longer than the good news: it also explains what went wrong.
const HANDOFF_MS = { success: 3000, staleSession: 5000 };

export default function ProfileForm(props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery('(min-width: 992px)'); // 992px = md

  const styles = {
    typography: {
      secondary: {
        color:
          theme.palette.type === 'light'
            ? 'rgba(0, 0, 0, 0.6)'
            : 'rgba(255, 255, 255, 0.6)',
      },
    },
    iconComponent: {
      color:
        theme.palette.type === 'dark' ? 'white' : theme.palette.primary.main,
    },
    iconButton: {
      ':hover': {
        backgroundColor:
          theme.palette.type === 'dark'
            ? `${theme.palette.primary.main}33`
            : `{theme.palette.grey[400]}33`,
      },
    },
  };

  const {
    userProfile,
    isLoading,
    setIsLoading,
    patchProfile,
    getSessionCookie,
    logOut,
    submit,
    setSubmit,
    nistCheckPassword,
    hasValidatePasswordResponse,
    canCreateAccount,
    resolver,
    setResolver,
    open,
    setOpen,
    setErrorNew,
    setErrorNewMsg,
    errorNew,
    errorNewMsg,
    newPassword,
    setNewPassword,
    confirmNewPassword,
    setConfirmNewPassword,
    errorPasswordConfirmation,
    setErrorPasswordConfirmation,
  } = props;
  const [name, setName] = useState('');
  const [lastNames, setLastNames] = useState('');
  const [contact, setContact] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState(false);
  const [errorCurrent, setErrorCurrent] = useState(false);
  const [language, setLanguage] = useState('en');
  const [mode, setMode] = useState('update');
  const [openConfirmation, setOpenConfirmation] = useState(false);
  // Signing out is irreversible — inside a WebView it sends the host app back
  // to the login screen — so it happens once however often an effect re-runs this.
  const handoffInFlight = useRef(false);

  // The page's own channel for a save that failed after the user already
  // authenticated; the helpers under the inputs belong to the password rules.
  const showError = key => {
    toast.dismissAll();
    toast.error(t(key));
  };

  /**
   * The last thing this page says before it leaves. The sign-out rides on the
   * toast's own dismissal — its timer, a swipe or the close button — so the
   * message is never cut short and never outlives being read.
   */
  const handOffTo = (tone, translationKey, duration) => {
    handoffInFlight.current = true;
    // patchProfile drove this flag back down on its way through, and nothing
    // on this page is actionable once the sign-out is pending.
    setIsLoading(true);
    toast.dismissAll();
    toast[tone]({ title: t(translationKey), duration, onDismiss: logOut });
  };

  // A fact about the profile, not state an effect may not have set.
  const hasPasswordProvider = (userProfile?.providerUserInfo || []).some(
    provider => provider.providerId === 'password',
  );

  const setUpRecaptcha = () => {
    if (window.recaptchaVerifier) {
      return;
    }
    const auth = getAuth();
    auth.tenantId = userProfile.tenantId;
    auth.languageCode = language;
    window.recaptchaVerifier = new RecaptchaVerifier(
      'recaptcha',
      { size: 'invisible' },
      auth,
    );
  };

  const handleChangePhone = value => {
    setContact(value);
  };

  // The tel input forces a calling code (e.g. "+1") even when the user has no
  // real number, and Firebase rejects a bare calling code as INVALID_PHONE_NUMBER.
  // Only send a complete, valid number; otherwise send '' so the backend skips it.
  const getPhoneNumberPayload = () => (matchIsValidTel(contact) ? contact : '');

  const handleClickShowCurrentPassword = () =>
    setShowCurrentPassword(show => !show);

  const handleClickShowNewPassword = () => setShowNewPassword(show => !show);

  const handleClickShowPasswordConfirmation = () =>
    setShowPasswordConfirmation(show => !show);

  const handleMouseDownPasswordCurrent = event => event.preventDefault();

  const handleMouseDownPasswordNew = event => event.preventDefault();

  const handleMouseDownPasswordConfirmation = event => event.preventDefault();

  const handleUpdateProfile = async () => {
    setErrorCurrent(false);
    setErrorNew(false);
    setErrorPasswordConfirmation(false);
    setErrorNewMsg('');
    if (newPassword !== confirmNewPassword) {
      setSubmit(false);
      setErrorPasswordConfirmation(true);
    } else {
      if (newPassword === '' && currentPassword === '') {
        await patchProfile({
          uid: userProfile.uid,
          tenantId: userProfile.tenantId,
          name: name,
          lastNames: lastNames,
          phoneNumber: getPhoneNumberPayload(),
        });
        setSubmit(false);
      } else {
        sessionStorage.setItem('newPassword', newPassword);
        await nistCheckPassword({
          password: newPassword,
          info: userProfile.email,
          tenantId: userProfile.tenantId,
        });
        setSubmit(false);
      }
    }
  };

  const handleGenerateNewPassword = () => {
    const generated = generatePassword();
    setShowNewPassword(true);
    setShowPasswordConfirmation(true);
    setNewPassword(generated);
    setConfirmNewPassword(generated);
  };

  // A password change revokes the session cookie this page shares with the
  // consumer app; only the client SDK hands back tokens to re-mint it with.
  const changePassword = async () => {
    try {
      await updatePassword(getAuth().currentUser, newPassword);
    } catch (error) {
      console.error(
        'ProfileForm.changePassword:',
        getSafeErrorIdentifier(error),
      );
      return { ok: false, errorKey: GENERIC_ERROR };
    }
    // Straight from the user the SDK just re-authenticated.
    try {
      if (await getSessionCookie(await getCurrentUserTokens())) {
        return { ok: true };
      }
    } catch (error) {
      console.error('ProfileForm.renewSession:', getSafeErrorIdentifier(error));
    }
    // The password did change, so "try again" would be a lie: the session is
    // the only thing left undone, and logging in again is what fixes it.
    return { ok: false, staleSession: true };
  };

  /**
   * Saves the profile, changes the password, renews the session, and only then
   * signs the user out so the next login runs on the new password.
   *
   * @returns {Promise<{ok: boolean, errorKey?: string}>} Reported rather than
   *   thrown, so the MFA dialog can stay open on a failure it caused none of.
   *   `ok` answers for the password change alone: a change that landed on a
   *   session that could not be renewed is reported on the page instead.
   */
  const handleUpdateProfileWithPassword = async () => {
    if (handoffInFlight.current) return { ok: true };
    toast.dismissAll();
    setIsLoading(true);

    // The fields first and without the password: this request still needs the
    // live session, and the change below is what revokes it.
    const saved = await patchProfile({
      uid: userProfile.uid,
      tenantId: userProfile.tenantId,
      name: name,
      lastNames: lastNames,
      phoneNumber: getPhoneNumberPayload(),
    });

    const changed = saved
      ? await changePassword()
      : { ok: false, errorKey: GENERIC_ERROR };

    if (changed.staleSession) {
      // The change itself landed, so the challenge did its job and the dialog
      // closes: this page owns what is left and says it here, not in there.
      handOffTo('error', SESSION_NOT_RENEWED, HANDOFF_MS.staleSession);
      return { ok: true };
    }

    if (!changed.ok) {
      // Staying put is the point: a failed save that still signed the user out
      // would report success by leaving.
      setIsLoading(false);
      showError(changed.errorKey);
      return { ok: false, errorKey: changed.errorKey };
    }

    // The renewed session is spent on the sign-out itself: the password the
    // calling app holds is stale, so it has to be re-established by a login.
    handOffTo('success', PASSWORD_UPDATED, HANDOFF_MS.success);
    return { ok: true };
  };

  /**
   * Saves the profile and links an email/password credential to the account the
   * caller just re-authenticated, then reloads so the new provider shows up.
   *
   * @param {object} [userCredential] What re-authenticating handed back. Read in
   *   preference to `auth.currentUser`, which is a field on a shared object this
   *   page mutates.
   * @returns {Promise<{ok: boolean, errorKey?: string}>} Awaited by the MFA
   *   dialog, which stays open and busy until the link itself lands: the chain
   *   used to be left running, so a failed link closed it looking like a success.
   */
  const handleCreateEmailAccount = async userCredential => {
    const auth = getAuth();
    auth.tenantId = userProfile.tenantId;
    setIsLoading(true);
    const new_password = sessionStorage.getItem('newPassword');
    const credential = EmailAuthProvider.credential(
      userProfile.email,
      new_password,
    );

    await patchProfile({
      uid: userProfile.uid,
      tenantId: userProfile.tenantId,
      name: name,
      lastNames: lastNames,
      phoneNumber: getPhoneNumberPayload(),
    });

    try {
      await linkWithCredential(
        userCredential?.user ?? auth.currentUser,
        credential,
      );
    } catch (error) {
      console.error(
        'ProfileForm.handleCreateEmailAccount:',
        getSafeErrorIdentifier(error),
      );
      setIsLoading(false);
      return { ok: false, errorKey: GENERIC_ERROR };
    }

    sessionStorage.removeItem('newPassword');
    // The spinner stays up on purpose: this page is being replaced, and there
    // is nothing on it the user should be able to touch on the way out.
    window.location.reload();
    return { ok: true };
  };

  /** Reports a failed link on the page, for the paths that run with no dialog up. */
  const reportIfFailed = result => {
    if (result && result.ok === false) showError(result.errorKey);
  };

  useEffect(() => {
    if (hasValidatePasswordResponse) {
      if (hasValidatePasswordResponse.isValidPassword) {
        // Closing the MFA dialog drops the verifier, and only re-authenticating
        // can reopen it: re-arm here or the retry sends the dialog a dead one.
        setUpRecaptcha();
        if (!hasPasswordProvider) {
          const auth = getAuth();
          const provider = new GoogleAuthProvider();
          auth.tenantId = userProfile.tenantId;
          auth.languageCode = language;
          sessionStorage.setItem('action', 'createEmailAccount');
          if (window.location.hostname !== 'localhost') {
            signInWithRedirect(auth, provider);
          } else {
            signInWithPopup(auth, provider)
              .then(result => {
                if (result) {
                  handleCreateEmailAccount(result).then(reportIfFailed);
                }
              })
              .catch(error => {
                if (error.code === 'auth/multi-factor-auth-required') {
                  // Never log this error: it carries `mfaPendingCredential`
                  // in `customData._serverResponse`.
                  setResolver(getMultiFactorResolver(auth, error));
                  props.setOpenOnProfile(true);
                } else {
                  console.error(
                    'ProfileForm.createEmailAccountReauth:',
                    getSafeErrorIdentifier(error),
                  );
                }
              });
          }
        } else {
          const auth = getAuth();
          auth.tenantId = userProfile.tenantId;
          setIsLoading(true);
          signInWithEmailAndPassword(auth, userProfile.email, currentPassword)
            .then(() => {
              handleUpdateProfileWithPassword();
            })
            .catch(error => {
              if (error.code === 'auth/wrong-password') {
                setErrorCurrent(true);
                setIsLoading(false);
              } else if (error.code === 'auth/multi-factor-auth-required') {
                setResolver(getMultiFactorResolver(auth, error));
                setOpen(true);
              } else {
                // Anything else used to leave the spinner up with no message.
                setIsLoading(false);
                showError(GENERIC_ERROR);
                console.error(
                  'ProfileForm.reauth:',
                  getSafeErrorIdentifier(error),
                );
              }
            });
        }
      } else {
        setErrorNew(true);
        setErrorNewMsg(hasValidatePasswordResponse.message);
        setIsLoading(false);
      }
    }
  }, [hasValidatePasswordResponse]);

  useEffect(() => {
    if (!userProfile) {
      return;
    }
    const displayName = userProfile.displayName || '';
    setName(displayName.split(' ')[0]);
    setLastNames(displayName.substring(displayName.split(' ')[0].length + 1));
    setContact(userProfile.phoneNumber || '');
    setUpRecaptcha();
  }, [userProfile]);

  useEffect(() => {
    if (submit) {
      handleUpdateProfile();
    }
  }, [submit]);

  useEffect(() => {
    let languages = ['en', 'es', 'de'];
    let userLanguage = navigator.language || navigator.userLanguage || 'en-US';
    userLanguage = userLanguage.split('-')[0];
    userLanguage = languages.includes(userLanguage) ? userLanguage : 'en';

    setLanguage(userLanguage);
  }, []);

  useEffect(() => {
    if (canCreateAccount) {
      handleCreateEmailAccount().then(reportIfFailed);
    }
  }, [canCreateAccount]);

  useEffect(() => {
    if (resolver) {
      if (sessionStorage.getItem('action') === 'createEmailAccount') {
        sessionStorage.removeItem('action');
        setMode('create');
      }
    }
  }, [resolver]);

  return (
    <>
      <Grid item xs={12} md={6} paddingY={2} paddingX={4}>
        <Grid item xs={12}>
          {isLoading ? (
            <Skeleton animation="wave" height={24} width="40%" />
          ) : (
            <Typography textAlign={'left'} variant="h6">
              {t('heimdal_app.Profile.user_information')}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} marginY={'1em'}>
          {isLoading ? (
            <Skeleton animation="wave" height={24} width="30%" />
          ) : (
            <Typography textAlign={'left'} variant="subtitle2">
              {t('heimdal_app.Login.email')}
            </Typography>
          )}
          {isLoading ? (
            <Skeleton animation="wave" height={40} width="100%" />
          ) : (
            <StyledOutlinedInput
              fullWidth
              size="small"
              variant="outlined"
              value={userProfile?.email ? userProfile.email : ''}
              disabled
            />
          )}
        </Grid>
        <Grid item xs={12} marginY={'1em'}>
          {isLoading ? (
            <Skeleton animation="wave" height={24} width="30%" />
          ) : (
            <Typography textAlign={'left'} variant="subtitle2">
              {t('heimdal_app.Profile.name')}
            </Typography>
          )}
          {isLoading ? (
            <Skeleton animation="wave" height={40} width="100%" />
          ) : (
            <StyledOutlinedInput
              fullWidth
              size="small"
              id="name"
              name="name"
              variant="outlined"
              value={name}
              onChange={e => {
                const value = e.target.value;
                if (value.match(/^[/^\S*$/]*$/)) {
                  setName(value);
                }
              }}
            />
          )}
        </Grid>
        <Grid item xs={12} marginY={'1em'}>
          {isLoading ? (
            <Skeleton animation="wave" height={24} width="40%" />
          ) : (
            <Typography textAlign={'left'} variant="subtitle2">
              {t('heimdal_app.Profile.last_names')}
            </Typography>
          )}
          {isLoading ? (
            <Skeleton animation="wave" height={40} width="100%" />
          ) : (
            <StyledOutlinedInput
              fullWidth
              size="small"
              id="lastNames"
              name="lastNames"
              variant="outlined"
              value={lastNames}
              onChange={e => setLastNames(e.target.value)}
            />
          )}
        </Grid>
        <Grid item xs={12} marginY={'1em'}>
          {isLoading ? (
            <Skeleton animation="wave" height={24} width="40%" />
          ) : (
            <Typography textAlign={'left'} variant="subtitle2">
              {t('heimdal_app.Profile.contact')}
            </Typography>
          )}
          {isLoading ? (
            <Skeleton animation="wave" height={40} width="100%" />
          ) : (
            <StyledTelInput
              fullWidth
              size="small"
              value={contact}
              defaultCountry="US"
              onChange={handleChangePhone}
              forceCallingCode
              focusOnSelectCountry
              MenuProps={{
                sx: {
                  '& .MuiPaper-root': {
                    backgroundColor:
                      theme.palette.type === 'light'
                        ? 'white'
                        : theme.palette.paper.main,
                    color: theme.palette.type === 'light' ? 'black' : 'white',
                  },
                  '.MuiTelInput-Typography-calling-code': {
                    color: theme.palette.type === 'light' ? 'black' : 'white',
                  },
                },
              }}
            />
          )}
        </Grid>
      </Grid>
      {isLargeScreen && (
        <Divider orientation="vertical" flexItem sx={{ mr: '-1px' }} />
      )}
      <Grid item xs={12} md={6} paddingY={2} paddingX={4}>
        <Grid item xs={12}>
          {isLoading ? (
            <Skeleton animation="wave" height={24} width="40%" />
          ) : (
            <Typography textAlign={'left'} variant="h6">
              {t('heimdal_app.Profile.password')}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} marginY={'1em'}>
          {isLoading ? (
            <Skeleton animation="wave" height={24} width="40%" />
          ) : hasPasswordProvider ? (
            <Typography textAlign={'left'} variant="subtitle2">
              {t('heimdal_app.Profile.current_password')}
            </Typography>
          ) : null}
          {isLoading ? (
            <Skeleton animation="wave" height={40} width="100%" />
          ) : hasPasswordProvider ? (
            <FormControl variant="outlined" fullWidth>
              <StyledOutlinedInput
                id="outlined-adornment-password"
                type={showCurrentPassword ? 'text' : 'password'}
                required
                error={errorCurrent}
                size={'small'}
                fullWidth
                name="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowCurrentPassword}
                      onMouseDown={handleMouseDownPasswordCurrent}
                      edge="end"
                      sx={styles.iconButton}>
                      {showCurrentPassword ? (
                        <IconComponent
                          iconName={'eye-off-outline'}
                          className={'icon'}
                          style={styles.iconComponent}
                        />
                      ) : (
                        <IconComponent
                          iconName={'eye-outline'}
                          className={'icon'}
                          style={styles.iconComponent}
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <FormHelperText error={errorCurrent}>
                {errorCurrent ? t('heimdal_app.Login.invalid_password') : ''}
              </FormHelperText>
            </FormControl>
          ) : null}
        </Grid>
        <Grid item xs={12} marginY={'1em'}>
          {isLoading ? (
            <Skeleton animation="wave" height={24} width="40%" />
          ) : (
            <Typography textAlign={'left'} variant="subtitle2">
              {t('heimdal_app.PassRecovery.new_password')}
            </Typography>
          )}
          {isLoading ? (
            <Skeleton animation="wave" height={40} width="100%" />
          ) : (
            <>
              <FormControl variant="outlined" fullWidth>
                <StyledOutlinedInput
                  id="outlined-adornment-password"
                  type={showNewPassword ? 'text' : 'password'}
                  required
                  error={errorNew}
                  fullWidth
                  size={'small'}
                  name="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder={t('heimdal_app.Profile.password_placeholder')}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowNewPassword}
                        onMouseDown={handleMouseDownPasswordNew}
                        edge="end"
                        sx={styles.iconButton}>
                        {showNewPassword ? (
                          <IconComponent
                            iconName={'eye-off-outline'}
                            className={'icon'}
                            style={styles.iconComponent}
                          />
                        ) : (
                          <IconComponent
                            iconName={'eye-outline'}
                            className={'icon'}
                            style={styles.iconComponent}
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                <FormHelperText error={errorNew}>
                  {errorNew ? t('heimdal_app.PassRecovery.' + errorNewMsg) : ''}
                </FormHelperText>
              </FormControl>
            </>
          )}
        </Grid>
        <Grid item xs={12} marginY={'1em'}>
          {isLoading ? (
            <Skeleton animation="wave" height={24} width="40%" />
          ) : (
            <Typography textAlign={'left'} variant="subtitle2">
              {t('heimdal_app.PassRecovery.confirm_password')}
            </Typography>
          )}
          {isLoading ? (
            <Skeleton animation="wave" height={40} width="100%" />
          ) : (
            <FormControl variant="outlined" fullWidth>
              <StyledOutlinedInput
                id="outlined-adornment-password"
                type={showPasswordConfirmation ? 'text' : 'password'}
                required
                error={errorPasswordConfirmation}
                fullWidth
                size={'small'}
                name="password"
                value={confirmNewPassword}
                onChange={e => setConfirmNewPassword(e.target.value)}
                placeholder={t('heimdal_app.Profile.password_placeholder')}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPasswordConfirmation}
                      onMouseDown={handleMouseDownPasswordConfirmation}
                      edge="end"
                      sx={styles.iconButton}>
                      {showPasswordConfirmation ? (
                        <IconComponent
                          iconName={'eye-off-outline'}
                          className={'icon'}
                          style={styles.iconComponent}
                        />
                      ) : (
                        <IconComponent
                          iconName={'eye-outline'}
                          className={'icon'}
                          style={styles.iconComponent}
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <FormHelperText error={errorPasswordConfirmation}>
                {errorPasswordConfirmation
                  ? t('heimdal_app.PassRecovery.password_match')
                  : ''}
              </FormHelperText>
              <Tooltip
                title={t('heimdal_app.Profile.generate_new_password_info')}
                arrow
                placement="right">
                <Typography
                  variant="body2"
                  align="right"
                  onClick={handleGenerateNewPassword}
                  style={{ userSelect: 'none' }}
                  sx={styles.typography.secondary}
                  onMouseOver={e => {
                    e.target.style.cursor = 'pointer';
                  }}
                  onMouseOut={e => {
                    e.target.style.cursor = 'default';
                  }}>
                  {t('heimdal_app.Profile.generate_new_password')}
                </Typography>
              </Tooltip>
            </FormControl>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          md={12}
          justifyContent={'flex-end'}
          alignItems={'flex-end'}
          paddingBottom={1}
          display={'flex'}>
          {isLoading ? (
            <Skeleton animation="wave" height={48} width="10%" />
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="small"
              style={{ textTransform: 'capitalize' }}
              onClick={() => {
                setOpenConfirmation(true);
              }}>
              <Typography variant="inherit">
                {t('heimdal_app.Profile.save')}
              </Typography>
            </Button>
          )}
        </Grid>
      </Grid>

      <MFAVerify
        open={open}
        setOpen={setOpen}
        resolver={resolver}
        userProfile={userProfile}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        onVerificationComplete={userCredential =>
          mode === 'create'
            ? handleCreateEmailAccount(userCredential)
            : mode === 'update'
            ? handleUpdateProfileWithPassword()
            : undefined
        }
      />
      <ConfirmationModal
        open={openConfirmation}
        setOpen={setOpenConfirmation}
        title={t('heimdal_app.Profile.user_information_update')}
        content={
          confirmNewPassword === '' && currentPassword === ''
            ? t('heimdal_app.Profile.update_profile_confirmation')
            : t('heimdal_app.Profile.update_profile_and_password_confirmation')
        }
        onVerificationComplete={() => {
          setSubmit(true);
          setOpenConfirmation(false);
        }}
      />
    </>
  );
}
